import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Check, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useDebounce } from '@/hooks/useDebounce';

interface MultiSelectProps {
  label?: string;
  options: Array<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxDisplay?: number;
}

/**
 * Multi-select dropdown with search and chips.
 * Used for selecting multiple events.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  maxDisplay = 3,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 150);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(debouncedSearch.toLowerCase()),
  );

  const toggle = (optValue: string) => {
    const next = value.includes(optValue)
      ? value.filter((v) => v !== optValue)
      : [...value, optValue];
    onChange(next);
  };

  const removeChip = (optValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optValue));
  };

  return (
    <div className="w-full relative" ref={containerRef}>
      {label && (
        <label className="block text-xs font-medium text-slate-600 mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setOpen(!open);
            setTimeout(() => inputRef.current?.focus(), 50);
          }
        }}
        disabled={disabled}
        className={cn(
          'w-full min-h-[36px] rounded-lg border bg-white px-2.5 py-1.5 text-left',
          'flex items-center gap-1.5 flex-wrap',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
          open ? 'border-brand-400 ring-2 ring-brand-500/20' : 'border-slate-200 hover:border-slate-300',
          disabled && 'opacity-50 cursor-not-allowed bg-slate-50',
        )}
      >
        {value.length === 0 ? (
          <span className="text-sm text-slate-400">{placeholder}</span>
        ) : (
          <>
            {value.slice(0, maxDisplay).map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 text-xs font-medium px-2 py-0.5 rounded-md"
              >
                {v}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-brand-900"
                  onClick={(e) => removeChip(v, e)}
                />
              </span>
            ))}
            {value.length > maxDisplay && (
              <span className="text-xs text-slate-400">
                +{value.length - maxDisplay} more
              </span>
            )}
          </>
        )}
        <ChevronDown className={cn(
          'w-4 h-4 text-slate-400 ml-auto shrink-0 transition-transform duration-150',
          open && 'rotate-180',
        )} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute z-50 mt-1 w-full max-h-64 overflow-hidden',
              'bg-white rounded-xl border border-slate-200 shadow-soft-md',
            )}
            style={{ minWidth: containerRef.current?.offsetWidth }}
          >
            {/* Search */}
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full h-8 pl-8 pr-3 text-sm bg-slate-50 border-none rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500/20"
                />
              </div>
            </div>

            {/* Options */}
            <div className="overflow-y-auto max-h-48 py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-slate-400">
                  No matches found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const selected = value.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggle(opt.value)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left',
                        'transition-colors duration-100',
                        selected
                          ? 'bg-brand-50/50 text-brand-700'
                          : 'text-slate-700 hover:bg-slate-50',
                      )}
                    >
                      <div
                        className={cn(
                          'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                          'transition-colors duration-150',
                          selected
                            ? 'bg-brand-500 border-brand-500'
                            : 'border-slate-300',
                        )}
                      >
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="truncate">{opt.label}</span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {value.length > 0 && (
              <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  {value.length} selected
                </span>
                <button
                  onClick={() => onChange([])}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Clear all
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
